/**
 * OutstandingVenueRequestsTable — list of pending venue-onboarding
 * request links (form_tokens).
 *
 * Lives on the Distribution page as of v10.19.51. Simple row-level
 * actions (Copy link, Edit expires, Delete) execute inline. The
 * heavier "Complete with cert" and "Fulfill manually" flows still
 * live on the Venues page; clicking those buttons navigates the
 * operator there with a query param that the Venues page picks up
 * to open the matching dialog.
 *
 * Renders nothing when there are no outstanding requests, so it
 * doesn't add visual noise on a quiet distribution day.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Box, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  AssignmentTurnedIn as CompleteIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { CANONICAL_ORIGIN } from '../config';


export default function OutstandingVenueRequestsTable({ onSnackbar }) {
  const navigate = useNavigate();
  const [links, setLinks] = React.useState([]);

  const reload = React.useCallback(async () => {
    try {
      const { data } = await api.get('/request/links');
      setLinks(data || []);
    } catch (_) {
      // Best-effort — silent failure keeps the rest of the distribution
      // page functional if the request-links endpoint is briefly down.
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const handleCopy = async (url, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(url);
      onSnackbar?.({ message: label, severity: 'success' });
    } catch (_) {
      onSnackbar?.({ message: 'Copy failed — select and copy manually', severity: 'error' });
    }
  };

  if (!links.length) return null;

  const pending = links.filter(r => !r.used_at).length;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Outstanding Requests ({pending} pending)
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5 }}>Title</TableCell>
              <TableCell sx={{ py: 0.5 }}>Recipient</TableCell>
              <TableCell sx={{ py: 0.5 }}>Sent</TableCell>
              <TableCell sx={{ py: 0.5 }}>Expires</TableCell>
              <TableCell sx={{ py: 0.5 }}>Status</TableCell>
              <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map(r => {
              const expired = r.expires_at && new Date(r.expires_at) < new Date();
              const status = r.used_at ? 'Submitted' : expired ? 'Expired' : 'Pending';
              const color = r.used_at ? 'success' : expired ? 'error' : 'warning';
              return (
                <TableRow key={r.id}>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption" fontWeight={500}>{r.title || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {r.recipient_name || '—'}
                      {r.recipient_email && <Box component="span" sx={{ color: 'text.secondary' }}> · {r.recipient_email}</Box>}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {r.created_at ? new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {!r.used_at && !expired ? (
                      <input
                        type="date"
                        defaultValue={r.expires_at ? new Date(r.expires_at).toISOString().split('T')[0] : ''}
                        style={{
                          background: 'transparent',
                          border: '1px solid rgba(232,230,227,0.15)',
                          borderRadius: 4,
                          color: 'inherit',
                          fontSize: '0.75rem',
                          padding: '2px 4px',
                          cursor: 'pointer',
                        }}
                        onChange={async (e) => {
                          if (!e.target.value) return;
                          try {
                            await api.patch(`/request/links/${r.id}`, {
                              expires_at: new Date(e.target.value + 'T23:59:59Z').toISOString(),
                            });
                            reload();
                          } catch (_) { /* swallow — best-effort */ }
                        }}
                      />
                    ) : (
                      <Typography variant="caption">
                        {r.expires_at ? new Date(r.expires_at).toLocaleString(undefined, { dateStyle: 'short' }) : '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip size="small" label={status} color={color} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }} align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {!expired && (
                        <Tooltip title={r.used_at
                          ? 'Add cert (venue replied via email after submitting)'
                          : 'Complete with cert (venue replied via email)'}>
                          <IconButton size="small" onClick={() => navigate(`/venues?complete=${r.id}`)}>
                            <CompleteIcon fontSize="small" sx={{ color: '#d4a017' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!r.used_at && (
                        <Tooltip title="Fulfill manually (add venue only, no cert)">
                          <IconButton size="small" onClick={() => navigate(`/venues?fulfill=${r.id}`)}>
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!r.used_at && !expired && (
                        <Tooltip title="Copy request link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(`${CANONICAL_ORIGIN}/request/${r.token}`, 'Link copied')}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete request">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (!window.confirm('Delete this request link?')) return;
                            try {
                              await api.delete(`/request/links/${r.id}`);
                              reload();
                            } catch (_) { /* swallow */ }
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
